import type { Customer, Item, ItemStatus, Part, Site } from '../../domain/inventory';
import { statusLabels } from '../../domain/status';
import { humanizeIdentifier } from '../../lib/formatting';
import { siteDisplayName } from '../../lib/site-label';

/** The dimensions the inventory breakdown bar can split by, in drill-down order. */
export type BreakdownDimension = 'partType' | 'status' | 'customer' | 'site';

export const breakdownDimensionLabels: Record<BreakdownDimension, string> = {
  partType: 'part type',
  status: 'status',
  customer: 'customer',
  site: 'assigned site',
};

/** Filters that are narrowed to one value, which decide the next dimension. */
export interface BreakdownScope {
  partTypes: readonly string[];
  partId: string;
  statuses: readonly ItemStatus[];
  customerIds: readonly string[];
  siteIds: readonly string[];
  assignedSiteId: string;
}

export interface BreakdownSegment {
  /** The filter value a click applies; empty for the "Other" and "None" buckets. */
  key: string;
  label: string;
  count: number;
}

export interface Breakdown {
  dimension: BreakdownDimension;
  total: number;
  segments: BreakdownSegment[];
}

export const MAX_BREAKDOWN_SEGMENTS = 6;

/**
 * The next logical dimension to split the current item set by: part type
 * until one is chosen, then status, then customer, then site. Null when
 * every dimension is already narrowed to one value.
 */
export function nextBreakdownDimension(scope: BreakdownScope): BreakdownDimension | null {
  if (scope.partTypes.length !== 1 && !scope.partId) return 'partType';
  if (scope.statuses.length !== 1) return 'status';
  if (scope.customerIds.length !== 1 && scope.siteIds.length === 0 && !scope.assignedSiteId) {
    return 'customer';
  }
  if (scope.siteIds.length !== 1 && !scope.assignedSiteId) return 'site';
  return null;
}

interface Lookups {
  partsById: ReadonlyMap<string, Part>;
  sitesById: ReadonlyMap<string, Site>;
  customersById: ReadonlyMap<string, Customer>;
}

function keyAndLabel(
  item: Item,
  dimension: BreakdownDimension,
  { partsById, sitesById, customersById }: Lookups,
): { key: string; label: string } {
  switch (dimension) {
    case 'partType': {
      const type = partsById.get(item.partId)?.type ?? '';
      return { key: type, label: type ? humanizeIdentifier(type) : 'Unknown part' };
    }
    case 'status': {
      const status = item.status ?? 'STATUS_UNSPECIFIED';
      return { key: status, label: statusLabels[status] };
    }
    case 'customer': {
      const customerId = sitesById.get(item.siteId)?.customerId ?? '';
      const customer = customersById.get(customerId);
      return customer
        ? { key: customerId, label: customer.displayName ?? customer.name }
        : { key: '', label: 'No customer' };
    }
    case 'site': {
      const site = sitesById.get(item.siteId);
      return site
        ? { key: site.id, label: siteDisplayName(site, site.id) }
        : { key: '', label: 'No site' };
    }
  }
}

/**
 * Counts items per value of the dimension, largest first. Values past the
 * first MAX_BREAKDOWN_SEGMENTS - 1 fold into one "Other" segment.
 */
export function computeBreakdown(
  items: readonly Item[],
  dimension: BreakdownDimension,
  lookups: Lookups,
): Breakdown {
  const counts = new Map<string, BreakdownSegment>();
  for (const item of items) {
    const { key, label } = keyAndLabel(item, dimension, lookups);
    const bucket = `${key}\u0000${label}`;
    const segment = counts.get(bucket);
    if (segment) segment.count += 1;
    else counts.set(bucket, { key, label, count: 1 });
  }
  const sorted = [...counts.values()].sort(
    (left, right) => right.count - left.count || left.label.localeCompare(right.label),
  );
  if (sorted.length <= MAX_BREAKDOWN_SEGMENTS) {
    return { dimension, total: items.length, segments: sorted };
  }
  const shown = sorted.slice(0, MAX_BREAKDOWN_SEGMENTS - 1);
  const rest = sorted.slice(MAX_BREAKDOWN_SEGMENTS - 1);
  const other: BreakdownSegment = {
    key: '',
    label: `Other (${rest.length})`,
    count: rest.reduce((sum, segment) => sum + segment.count, 0),
  };
  return { dimension, total: items.length, segments: [...shown, other] };
}
